import { jwtDecode } from 'jwt-decode';
import {
    createContext,
    ReactNode,
    useCallback,
    useMemo,
    useState,
} from 'react';
import { CircleJwtState } from './types';

export const CircleJwtContext = createContext<CircleJwtState>({
    authorizationJWTs: {},
    setAuthorizationJWT: () => {},
});

const CIRCLE_JWT_EXPIRATION_BUFFER = 1000 * 30; // 30 seconds
const CIRCLE_JWT_EXPIRATION_BACKSTOP = 1000 * 1; // 1 seconds

const getJwtExpirationRemainingTime = (
    jwt: string | undefined
): number | undefined => {
    if (!jwt) {
        return undefined;
    }

    const decodedJwt = jwtDecode(jwt);
    if (!decodedJwt.exp) {
        return undefined;
    }

    const now = new Date().getTime();
    return decodedJwt.exp * 1000 - now;
};

export function CircleJwtProvider({
    children,
}: {
    children: ReactNode | ReactNode[];
}) {
    const [authorizationJWTs, setAuthorizationJWTs] = useState<
        Record<string, string | undefined>
    >(() => ({}));
    const [isExpiredFlags, setIsExpiredFlags] = useState<
        Record<string, boolean | undefined>
    >({});
    const [jwtRefetchTimeoutIds] = useState<
        Record<string, NodeJS.Timeout | undefined>
    >(() => ({}));
    const [jwtBackstopTimeoutIds] = useState<
        Record<string, NodeJS.Timeout | undefined>
    >(() => ({}));
    const setAuthorizationJWT = useCallback(
        (
            circleId: number,
            jwt: string | undefined,
            expirationCallback: () => void
        ) => {
            // Set the JWT, and mark it as unexpired
            setAuthorizationJWTs((prev) => {
                if (prev[circleId] === jwt) {
                    return prev;
                }
                return {
                    ...prev,
                    [circleId]: jwt,
                };
            });
            if (jwt) {
                setIsExpiredFlags((prev) => {
                    if (!prev[circleId]) {
                        return prev;
                    }
                    return {
                        ...prev,
                        [circleId]: false,
                    };
                });
            }

            // Clear any existing timeouts for this circle
            if (jwtRefetchTimeoutIds[circleId]) {
                clearTimeout(jwtRefetchTimeoutIds[circleId]);
                delete jwtRefetchTimeoutIds[circleId];
            }
            if (jwtBackstopTimeoutIds[circleId]) {
                clearTimeout(jwtBackstopTimeoutIds[circleId]);
                delete jwtBackstopTimeoutIds[circleId];
            }

            // Set up new timeouts based on the JWT expiration
            const timeTilExpiry = getJwtExpirationRemainingTime(jwt);
            if (!timeTilExpiry) {
                return;
            }

            // Get a new Circle JWT before the current one expires
            const callbackTime = timeTilExpiry - CIRCLE_JWT_EXPIRATION_BUFFER;
            const refetchTimeoutId = setTimeout(
                expirationCallback,
                callbackTime <= 0 ? 0 : callbackTime
            );
            jwtRefetchTimeoutIds[circleId] = refetchTimeoutId;

            /**
             * As a backstop, also do not give expired JWTs to downstream consumers.
             * If the client has network and is functioning properly, this won't happen
             * because the above useEffect will refresh the JWT 30 seconds before it expires.
             * But if the client is offline or something else strange happens, this will prevent
             * downstream consumers from trying to use an expired JWT
             */
            const hardExpirationTime =
                timeTilExpiry - CIRCLE_JWT_EXPIRATION_BACKSTOP;
            const backstopTimeoutId = setTimeout(
                () => {
                    setIsExpiredFlags((prev) => {
                        if (prev[circleId]) {
                            return prev;
                        }
                        return {
                            ...prev,
                            [circleId]: true,
                        };
                    });
                },
                hardExpirationTime <= 0 ? 0 : hardExpirationTime
            );
            jwtBackstopTimeoutIds[circleId] = backstopTimeoutId;
        },
        [jwtBackstopTimeoutIds, jwtRefetchTimeoutIds]
    );

    const unexpiredAuthorizationJWTs = useMemo(() => {
        const unexpiredJwts: Record<string, string | undefined> = {};
        for (const [circleId, jwt] of Object.entries(authorizationJWTs)) {
            if (!isExpiredFlags[circleId]) {
                unexpiredJwts[circleId] = jwt;
            } else {
                unexpiredJwts[circleId] = undefined;
            }
        }
        return unexpiredJwts;
    }, [authorizationJWTs, isExpiredFlags]);

    return (
        <CircleJwtContext.Provider
            value={{
                authorizationJWTs: unexpiredAuthorizationJWTs,
                setAuthorizationJWT,
            }}
        >
            {children}
        </CircleJwtContext.Provider>
    );
}
